import CloseIcon from "@mui/icons-material/Close";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import {
  Typography,
  Box,
  Button,
  Paper,
  Modal,
  IconButton,
  Divider,
  TextField,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  useRoleListQuery,
  useLazyRoleGetQuery,
  usePermissionListQuery,
  useRoleUpdateMutation,
  useRoleCreateMutation,
} from "../api/api";
import { TCreateRoleForm } from "../types/api/common";
import { ConfigurableListItem } from "./ConfigurableListItem";
import { RelationsEditorModal } from "./RelationsEditorModal";
import { SearchField } from "./SearchField";
import { useListFilter } from "./useListFilter";

const bgColor = "#EDEDED";

const roleNamePattern = /^[А-Яа-яA-Za-z.:,\-\s]+$/;
const systemNamePattern = /^[A-Za-z.:\-\s]+$/;

export const RolesList = () => {
  const { enqueueSnackbar } = useSnackbar();

  const {
    data: rolesData,
    isLoading: isRolesLoading,
    isError: isRolesError,
  } = useRoleListQuery();

  const [
    getRole,
    { data: roleGetData, isLoading: isRoleGetLoading, isError: isRoleGetError },
  ] = useLazyRoleGetQuery();

  const {
    data: permissionListData,
    isLoading: isPermissionListLoading,
    isError: isPermissionListError,
  } = usePermissionListQuery();

  const [roleUpdate, { isLoading: isRoleUpdateLoading }] =
    useRoleUpdateMutation();

  const [roleCreate, { isLoading: isRoleCreateloading }] =
    useRoleCreateMutation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddRoleModalOpen, setIsAddRoleModalOpen] = useState(false);
  const [selectedRoleTitle, setSelectedRoleTitle] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);
  const roleRequestIdRef = useRef(0);

  const role = roleGetData?.data;

  const {
    search: roleSearch,
    setSearch: setRoleSearch,
    clearSearch: clearRoleSearch,
    filteredItems: filteredRoles,
    isFilteredEmpty: isRolesFilteredEmpty,
  } = useListFilter({
    items: rolesData?.data,
    getSearchValue: (roleItem) => roleItem.displayable_name || roleItem.name,
  });

  const {
    search: permissionSearch,
    setSearch: setPermissionSearch,
    clearSearch: clearPermissionSearch,
    filteredItems: filteredPermissions,
    isFilteredEmpty: isPermissionsFilteredEmpty,
  } = useListFilter({
    items: permissionListData?.data,
    getSearchValue: (permission) => permission.name,
  });

  const methods = useForm<TCreateRoleForm>({
    defaultValues: {
      displayable_name: "",
      name: "",
      description: "",
    },
    mode: "all",
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty, isValid, errors },
  } = methods;

  const handleOpenModal = async (id: number) => {
    const requestId = ++roleRequestIdRef.current;
    const roleItem = rolesData?.data.find((item) => item.id === id);

    setSelectedRoleTitle(roleItem?.displayable_name || roleItem?.name || "");
    setSelectedPermissions([]);
    setIsModalOpen(true);
    clearPermissionSearch();

    try {
      const response = await getRole(id).unwrap();

      if (requestId !== roleRequestIdRef.current) return;

      setSelectedPermissions(
        response.data.permissions.map((permission) => permission.id),
      );
    } catch {
      if (requestId !== roleRequestIdRef.current) return;
    }
  };

  const handleCloseModal = () => {
    roleRequestIdRef.current += 1;

    setIsModalOpen(false);
    setSelectedRoleTitle("");
    setSelectedPermissions([]);
    clearPermissionSearch();
  };

  const handleOpenAddRoleModal = () => {
    setIsAddRoleModalOpen(true);
  };

  const handleCloseAddRoleModal = () => {
    setIsAddRoleModalOpen(false);
    reset();
  };

  const handleTogglePermission = (permissionId: number) => {
    setSelectedPermissions((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId],
    );
  };

  const handleSaveNewRole = async (values: TCreateRoleForm) => {
    try {
      await roleCreate({
        name: values.name,
        displayable_name: values.displayable_name,
        description: values.description,
        permissions: [],
      }).unwrap();

      enqueueSnackbar("Успех", {
        variant: "success",
      });

      handleCloseAddRoleModal();
    } catch (e) {
      console.error("Ошибка при создании роли", e);

      enqueueSnackbar("Ошибка", {
        variant: "error",
      });
    }
  };

  const handleSave = async () => {
    if (!role) return;

    try {
      await roleUpdate({
        roleId: role.id,
        body: {
          name: role.name,
          displayable_name: role.displayable_name,
          description: role.description,
          permissions: selectedPermissions,
        },
      }).unwrap();

      enqueueSnackbar("Успех", {
        variant: "success",
      });

      handleCloseModal();
    } catch (e) {
      console.error("Ошибка при обновлении роли", e);

      enqueueSnackbar("Ошибка", {
        variant: "error",
      });
    }
  };

  const isSubmitNewRoleDisabled = !isDirty || !isValid || isRoleCreateloading;

  if (isRolesLoading) {
    return <Typography>Загрузка ролей...</Typography>;
  }

  if (isRolesError) {
    return <Typography color="error">Ошибка загрузки ролей</Typography>;
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 7,
            fontSize: "1.25rem",
            fontWeight: 500,
            color: "#5F6368",
          }}
        >
          Роли
        </Typography>

        <Button
          variant="contained"
          onClick={handleOpenAddRoleModal}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            px: 2,
            py: 2,
            bgcolor: "#00719CDE",
            boxShadow: "none",
            "&:hover": {
              bgcolor: "#13779d",
              boxShadow: "none",
            },
          }}
        >
          + Добавить роль
        </Button>
      </Box>

      <SearchField
        value={roleSearch}
        onChange={setRoleSearch}
        onClear={clearRoleSearch}
        placeholder="Поиск роли"
        sx={{ mb: 4 }}
      />

      <Paper
        elevation={3}
        sx={{
          p: 4.25,
          pr: 0,
          borderRadius: 3,
          bgcolor: "common.white",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            maxHeight: 615,
            overflowY: "auto",
          }}
        >
          {filteredRoles.map((roleItem) => (
            <ConfigurableListItem
              key={roleItem.id}
              id={roleItem.id}
              title={roleItem.displayable_name || roleItem.name}
              color={bgColor}
              onConfigure={handleOpenModal}
            />
          ))}

          {isRolesFilteredEmpty && (
            <Typography color="text.secondary">
              Нет подходящих данных
            </Typography>
          )}
        </Box>
      </Paper>

      <RelationsEditorModal
        open={isModalOpen}
        title="Роль"
        entityTitle={selectedRoleTitle}
        relationTitle="Пермишены"
        relationIcon={
          <SwapHorizIcon sx={{ fontSize: 36, color: "text.secondary" }} />
        }
        items={filteredPermissions}
        selectedIds={selectedPermissions}
        searchValue={permissionSearch}
        searchPlaceholder="Поиск пермишена"
        isFilteredEmpty={isPermissionsFilteredEmpty}
        isLoading={isRoleGetLoading || isPermissionListLoading}
        isError={isRoleGetError || isPermissionListError}
        isSaving={isRoleUpdateLoading}
        loadingText="Загрузка прав роли..."
        errorText="Ошибка загрузки прав роли"
        getItemLabel={(permission) => permission.name}
        onSearchChange={setPermissionSearch}
        onSearchClear={clearPermissionSearch}
        onToggle={handleTogglePermission}
        onSave={handleSave}
        onClose={handleCloseModal}
      />

      <Modal open={isAddRoleModalOpen} onClose={handleCloseAddRoleModal}>
        <Box
          component="form"
          onSubmit={handleSubmit(handleSaveNewRole)}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 560,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            outline: "none",
          }}
        >
          <IconButton
            onClick={handleCloseAddRoleModal}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ px: 3, py: 4 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
              Новая Роль
            </Typography>
          </Box>

          <Divider />

          <Box sx={{ px: 3, py: 3 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "180px 1fr",
                rowGap: 3,
                columnGap: 3,
                alignItems: "center",
              }}
            >
              <Typography sx={{ fontSize: 20, color: "#5F6368" }}>
                Название Роли
              </Typography>

              <Controller
                control={control}
                name="displayable_name"
                render={({ field: { ref, ...field }, fieldState }) => (
                  <TextField
                    inputRef={ref}
                    fullWidth
                    {...field}
                    placeholder="OPK, Специалисты"
                    helperText={fieldState.error?.message}
                    error={!!errors.displayable_name}
                    size="small"
                    inputProps={{ maxLength: 101 }}
                  />
                )}
                rules={{
                  required: "Обязательное поле",
                  maxLength: {
                    value: 100,
                    message: "Максимальная длина — 100 символов",
                  },
                  pattern: {
                    value: roleNamePattern,
                    message: "Допустимы только Aa-Яя, A-Z, . : , -",
                  },
                }}
              />

              <Typography sx={{ fontSize: 20, color: "#5F6368" }}>
                Системное наименование
              </Typography>

              <Controller
                control={control}
                name="name"
                render={({ field: { ref, ...field }, fieldState }) => (
                  <TextField
                    inputRef={ref}
                    fullWidth
                    {...field}
                    placeholder="ork.specialist"
                    helperText={fieldState.error?.message}
                    error={!!errors.name}
                    size="small"
                    inputProps={{ maxLength: 101 }}
                  />
                )}
                rules={{
                  required: "Обязательное поле",
                  maxLength: {
                    value: 100,
                    message: "Максимальная длина — 100 символов",
                  },
                  pattern: {
                    value: systemNamePattern,
                    message: "Допустимы только Aa-Zz . : -",
                  },
                }}
              />

              <Typography
                sx={{ fontSize: 20, color: "#5F6368", alignSelf: "flex-start" }}
              >
                Описание
              </Typography>

              <Controller
                control={control}
                name="description"
                render={({ field: { ref, ...field }, fieldState }) => (
                  <TextField
                    inputRef={ref}
                    fullWidth
                    {...field}
                    helperText={fieldState.error?.message}
                    error={!!errors.description}
                    size="small"
                    inputProps={{ maxLength: 255 }}
                    multiline
                    rows={4}
                  />
                )}
                rules={{
                  maxLength: {
                    value: 255,
                    message: "Максимальная длина — 255 символов",
                  },
                }}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 4,
              }}
            >
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitNewRoleDisabled}
                sx={{
                  px: 4,
                  py: 1.5,
                  textTransform: "none",
                  borderRadius: 1.5,
                  bgcolor: "#2388A8",
                  "&:hover": {
                    bgcolor: "#1d7895",
                  },
                }}
              >
                {isRoleCreateloading ? "Сохранение..." : "Сохранить"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};
